/*
 * sounds.ts — tiny self-contained sound engine for the game table.
 *
 * There are no audio asset files. Every sound is synthesized on the fly with
 * the Web Audio API, so the whole thing ships as a few hundred bytes of code
 * and needs nothing to load. The palette is deliberately quiet and short — a
 * "poker table" feel: cards sliding on felt, a chip tick, a soft notification
 * ding. Keep new sounds in the same register (low gain, fast decay).
 *
 * Autoplay: browsers won't let an AudioContext make noise until the user has
 * interacted with the page. We create the context lazily and resume it on the
 * first pointer/key event, then again defensively before each sound.
 */

type SoundName = 'card' | 'bid' | 'turn' | 'setEnd';

const VOLUME_KEY = 'setback:sound-volume';

// Master volume ceiling. The user's 0..1 volume is scaled by this so that even
// at full it stays soft and never overbearing.
const MASTER_CEILING = 0.5;

// Volume used when the user hasn't set one yet (0..1).
const DEFAULT_VOLUME = 0.7;

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let gestureHooked = false;

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n);

/** User-facing volume, 0 (silent) .. 1 (full). Persisted in localStorage. */
export const getVolume = (): number => {
  try {
    const raw = localStorage.getItem(VOLUME_KEY);
    if (raw === null) return DEFAULT_VOLUME;
    const n = parseFloat(raw);
    return Number.isFinite(n) ? clamp01(n) : DEFAULT_VOLUME;
  } catch {
    return DEFAULT_VOLUME;
  }
};

export const setVolume = (volume: number): void => {
  const v = clamp01(volume);
  try {
    localStorage.setItem(VOLUME_KEY, String(v));
  } catch {
    /* ignore */
  }
  // Apply live so a slider drag is audible immediately.
  if (master) master.gain.value = v * MASTER_CEILING;
};

// Wire a one-time listener so the audio context comes alive on the user's first
// interaction (satisfies the autoplay gesture requirement). Safe to call often.
export const primeAudioOnUserGesture = (): void => {
  if (gestureHooked || typeof window === 'undefined') return;
  gestureHooked = true;
  const resume = () => {
    ensureContext();
    ctx?.resume().catch(() => {});
    window.removeEventListener('pointerdown', resume);
    window.removeEventListener('keydown', resume);
  };
  window.addEventListener('pointerdown', resume);
  window.addEventListener('keydown', resume);
};

const ensureContext = (): AudioContext | null => {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = getVolume() * MASTER_CEILING;
    master.connect(ctx.destination);
  }
  return ctx;
};

// A short burst of white noise, useful as the raw material for card/chip sounds.
const noiseBuffer = (context: AudioContext, seconds: number): AudioBuffer => {
  const length = Math.max(1, Math.floor(context.sampleRate * seconds));
  const buffer = context.createBuffer(1, length, context.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  return buffer;
};

// A soft pure tone with a gentle attack/decay envelope.
const tone = (
  context: AudioContext,
  out: AudioNode,
  freq: number,
  start: number,
  duration: number,
  peak: number,
  type: OscillatorType = 'sine',
): void => {
  const osc = context.createOscillator();
  const gain = context.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(peak, start + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  osc.connect(gain).connect(out);
  osc.start(start);
  osc.stop(start + duration + 0.02);
};

// A filtered noise blip — the felt/chip texture.
const blip = (
  context: AudioContext,
  out: AudioNode,
  start: number,
  duration: number,
  peak: number,
  filterType: BiquadFilterType,
  freq: number,
  q = 0.7,
): void => {
  const src = context.createBufferSource();
  src.buffer = noiseBuffer(context, duration + 0.02);
  const filter = context.createBiquadFilter();
  filter.type = filterType;
  filter.frequency.value = freq;
  filter.Q.value = q;
  const gain = context.createGain();
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(peak, start + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  src.connect(filter).connect(gain).connect(out);
  src.start(start);
  src.stop(start + duration + 0.02);
};

const renderers: Record<SoundName, (c: AudioContext, out: AudioNode, t: number) => void> = {
  // Card on felt: a quick soft slide/flick — bandpassed noise, fast decay.
  card: (c, out, t) => {
    blip(c, out, t, 0.09, 0.22, 'bandpass', 2600, 0.8);
  },
  // Bid: a dry poker-chip tick — a very short high tick.
  bid: (c, out, t) => {
    blip(c, out, t, 0.04, 0.18, 'highpass', 3200, 0.5);
    tone(c, out, 1400, t, 0.05, 0.06, 'triangle');
  },
  // Your turn: a gentle, unmistakable notification ding.
  turn: (c, out, t) => {
    tone(c, out, 880, t, 0.22, 0.16);
  },
  // Set ends: a soft two-note chime (C5 → G5).
  setEnd: (c, out, t) => {
    tone(c, out, 523.25, t, 0.28, 0.14);
    tone(c, out, 783.99, t + 0.12, 0.34, 0.14);
  },
};

export const playSound = (name: SoundName): void => {
  if (getVolume() <= 0) return;
  const context = ensureContext();
  if (!context || !master) return;
  if (context.state === 'suspended') context.resume().catch(() => {});
  try {
    renderers[name](context, master, context.currentTime);
  } catch {
    /* audio is best-effort; never let it break gameplay */
  }
};
